import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Building, Newspaper, Loader2, Check, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PressPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isFormValid = formData.firstName.trim() && formData.email.trim() && formData.reason.trim();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/press/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to send inquiry. Please try again.');
      }
    } catch (err) {
      setError('Failed to send inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title="Press & Media Inquiries"
        description="Contact AMP Predict (ANJO LC) for press, interview requests, and media partnerships. Maryland-based prediction market platform."
        keywords="AMP Predict press, prediction market media, ANJO LC press, interview request, media partnership"
        url="https://amppredict.com/press"
      />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-[#F97316]/20 rounded-xl">
            <Newspaper className="h-6 w-6 text-[#F97316]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Press & Media</h1>
            <p className="text-gray-400 text-sm">Media inquiries and press contact</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Contact Information</h2>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#3B82F6]/10 rounded-xl">
                  <Mail className="h-5 w-5 text-[#3B82F6]" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Email</h3>
                  <a 
                    href="mailto:amppress@anjolc.com" 
                    className="text-[#3B82F6] hover:underline"
                  >
                    amppress@anjolc.com
                  </a>
                  <p className="text-gray-500 text-sm mt-1">
                    We typically respond within 24-48 hours
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#F97316]/10 rounded-xl">
                  <Building className="h-5 w-5 text-[#F97316]" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Company</h3>
                  <p className="text-gray-300">ANJO LC</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 bg-green-500/10 rounded-xl">
                  <MapPin className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Location</h3>
                  <p className="text-gray-300">Maryland, USA</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
            <h2 className="text-lg font-semibold text-white mb-6">Quick Links</h2>
            
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => navigate('/about')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                About Us
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/contact')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                Contact Us
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/terms')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                Terms of Service
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/privacy')}
                className="w-full justify-start border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] hover:text-white"
              >
                Privacy Policy
              </Button>
            </div>

            <div className="mt-6 p-4 bg-[#1e2a45]/50 rounded-xl">
              <p className="text-gray-400 text-sm">
                For press inquiries, interview requests, or media partnerships, please fill out the form below or email us directly at 
                <a href="mailto:amppress@anjolc.com" className="text-[#3B82F6] mx-1">
                  amppress@anjolc.com
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Press Inquiry Form */}
        <div className="mt-8 bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
          <h2 className="text-lg font-semibold text-white mb-6">Press Inquiry Form</h2>
          
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-[#3B82F6]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-[#3B82F6]" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Inquiry Sent Successfully!</h3>
              <p className="text-gray-400">
                Thank you for your press inquiry. We'll get back to you within 24-48 hours at {formData.email}.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <Input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter your first name"
                    className="bg-[#1e2a45] border-[#2d3a56] text-white"
                    data-testid="press-first-name"
                    required
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">
                    Last Name
                  </label>
                  <Input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter your last name"
                    className="bg-[#1e2a45] border-[#2d3a56] text-white"
                    data-testid="press-last-name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">
                    Email Address <span className="text-red-400">*</span>
                  </label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email address"
                    className="bg-[#1e2a45] border-[#2d3a56] text-white"
                    data-testid="press-email"
                    required
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">
                    Phone Number <span className="text-gray-500">(Optional)</span>
                  </label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="bg-[#1e2a45] border-[#2d3a56] text-white"
                    data-testid="press-phone"
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-300 text-sm mb-2 block">
                  Reason for Press Inquiry <span className="text-red-400">*</span>
                </label>
                <Textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Please describe the reason for your press inquiry, interview request, or media partnership proposal..."
                  className="bg-[#1e2a45] border-[#2d3a56] text-white min-h-[120px]"
                  data-testid="press-reason"
                  required
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <div className="flex justify-center pt-4">
                <Button
                  type="submit"
                  disabled={!isFormValid || submitting}
                  className={`px-8 py-3 text-white font-medium transition-all ${
                    submitted 
                      ? 'bg-[#3B82F6] hover:bg-[#2563EB]' 
                      : isFormValid 
                        ? 'bg-[#F97316] hover:bg-[#EA580C]' 
                        : 'bg-gray-600 cursor-not-allowed'
                  }`}
                  data-testid="press-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-5 w-5 mr-2" />
                      Send Press Inquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PressPage;
